import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Camera, TrendingUp, Instagram } from 'lucide-react';
import { ImageWithFallback } from './ImageWithFallback';
import './MostPhotographed.css';

function MostPhotographed({ period = 'week' }) {
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMostPhotographed();
  }, [period]);

  const fetchMostPhotographed = async () => {
    try {
      setLoading(true);
      
      // Use mock data that matches the design
      const mockDishesData = [
        {
          id: '1',
          dishName: 'Truffle Burger',
          restaurant: 'The Burger Lab',
          location: 'Brooklyn, NY',
          photoCount: 1247,
          mainImage: 'https://images.unsplash.com/photo-1676300187485-8bd01a71d755?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidXJnZXIlMjBjbG9zZSUyMHVwJTIwZ291cm1ldHxlbnwxfHx8fDE3NjQwOTk0NTl8MA&ixlib=rb-4.1.0&q=80&w=1080',
          rating: 4.9,
          trendingRank: 1,
        },
        {
          id: '2',
          dishName: 'Tonkotsu Ramen',
          restaurant: 'Ramen House',
          location: 'Manhattan, NY',
          photoCount: 1089,
          mainImage: 'https://images.unsplash.com/photo-1672303185512-399a42b2cf4a?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxyYW1lbiUyMGJvd2wlMjBhcnRpc3RpYyUyMHBob3RvfGVufDF8fHx8MTc2NDA5OTQ1OXww&ixlib=rb-4.1.0&q=80&w=1080',
          rating: 4.8,
          trendingRank: 2,
        },
        {
          id: '3',
          dishName: 'Margherita Pizza',
          restaurant: 'Bella Napoli',
          location: 'Brooklyn, NY',
          photoCount: 987,
          mainImage: 'https://images.unsplash.com/photo-1667207394004-acb6aaf4790e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwaXp6YSUyMG1hcmdoZXJpdGElMjBjbG9zZSUyMHVwfGVufDF8fHx8MTc2NDA5ODA5NHww&ixlib=rb-4.1.0&q=80&w=1080',
          rating: 4.8,
          trendingRank: 3,
        },
      ];

      setDishes(mockDishesData);
    } catch (error) {
      console.error('Error fetching most photographed:', error);
      setDishes([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="py-16">
        <div className="container mx-auto px-8 max-w-7xl">
          <div className="text-center">
            <p className="text-muted-foreground">Loading most photographed dishes...</p>
          </div>
        </div>
      </div>
    );
  }

  if (dishes.length === 0) {
    return null;
  }

  // Dish of the Week - highest photo count
  const dishOfTheWeek = dishes[0];

  return (
    <div className="py-16">
      <div className="container mx-auto px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <Camera className="w-6 h-6" />
            <p className="text-xs uppercase tracking-widest text-muted-foreground">
              Community Favorites
            </p>
          </div>
          <h2 className="serif text-6xl mb-3">Most Photographed Dishes</h2>
          <p className="text-muted-foreground">
            The dishes everyone can't stop snapping
          </p>
        </div>

        {/* Dish of the Week - Featured */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="border-2 border-foreground mb-12"
        >
          <Link to={`/dish/${dishOfTheWeek.id}`}>
            <div className="grid grid-cols-2">
              {/* Image */}
              <div className="relative aspect-[4/3]">
                <ImageWithFallback
                  src={dishOfTheWeek.mainImage}
                  alt={dishOfTheWeek.dishName}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-6 left-6 bg-foreground text-background px-4 py-2 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-xs uppercase tracking-widest">Dish of the Week</span>
                </div>
              </div>

              {/* Content */}
              <div className="p-12 flex flex-col justify-center">
                <div className="w-1 h-12 bg-foreground mb-6"></div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-foreground text-background flex items-center justify-center">
                    <span className="text-sm">#{dishOfTheWeek.trendingRank}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">★</span>
                    <span className="text-sm">{dishOfTheWeek.rating}</span>
                  </div>
                </div>

                <h3 className="serif text-5xl mb-3">{dishOfTheWeek.dishName}</h3>
                <p className="text-sm mb-2">{dishOfTheWeek.restaurant}</p>
                <p className="text-xs text-muted-foreground mb-8">{dishOfTheWeek.location}</p>

                <div className="bg-stone-50 border border-border p-6 mb-8">
                  <div className="flex items-center gap-4">
                    <Camera className="w-8 h-8 text-muted-foreground" />
                    <div>
                      <p className="serif text-4xl mb-1">{dishOfTheWeek.photoCount.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground uppercase tracking-widest">
                        Photos Shared
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Instagram className="w-5 h-5 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">
                    Tagged with #RatedBiteBurger
                  </p>
                </div>
              </div>
            </div>
          </Link>
        </motion.div>

        {/* Top 3 Grid */}
        {dishes.length > 1 && (
          <div>
            <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-6">
              This Week's Top 3
            </h3>
            <div className="grid grid-cols-3 gap-8">
              {dishes.map((dish, index) => (
                <motion.article
                  key={dish.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="border border-border group hover:border-foreground transition-colors cursor-pointer"
                >
                  <Link to={`/dish/${dish.id}`} className="block">
                    <div className="relative aspect-square overflow-hidden">
                      <ImageWithFallback
                        src={dish.mainImage}
                        alt={dish.dishName}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      
                      {/* Rank Badge */}
                      <div className="absolute top-4 left-4 w-12 h-12 bg-foreground text-background flex items-center justify-center">
                        <span className="serif text-2xl">#{dish.trendingRank}</span>
                      </div>

                      {/* Rating Badge */}
                      <div className="absolute top-4 right-4 bg-background px-3 py-1.5">
                        <span className="text-xs">★ {dish.rating.toFixed(1)}</span>
                      </div>

                      {/* Photo Count Overlay */}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                        <div className="flex items-center gap-2 text-white">
                          <Camera className="w-4 h-4" />
                          <span className="text-sm">{dish.photoCount.toLocaleString()} photos</span>
                        </div>
                      </div>
                    </div>

                    <div className="p-6">
                      <h4 className="serif text-2xl mb-2 group-hover:text-muted-foreground transition-colors">
                        {dish.dishName}
                      </h4>
                      <p className="text-sm mb-1">{dish.restaurant}</p>
                      <p className="text-xs text-muted-foreground">{dish.location}</p>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          </div>
        )}

        {/* Instagram CTA */}
        <div className="mt-12 border border-border bg-stone-50 p-8 text-center">
          <Instagram className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="serif text-3xl mb-3">Get Featured</h3>
          <p className="text-sm text-muted-foreground max-w-xl mx-auto">
            Tag your food photos with <strong>#RatedBite</strong> and the dish-specific hashtag to be featured in our Most Photographed Dishes section. The more photos, the higher the ranking!
          </p>
        </div>
      </div>
    </div>
  );
}

export default MostPhotographed;
